// src/components/Assets/AssetExpenses/AddExpenseForm.js
// Form for adding asset expenses

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes, FaTools, FaDollarSign, FaCalendar, FaTag, FaStickyNote } from "react-icons/fa";
import PropTypes from "prop-types";
import { ExpenseCategories } from "../models";
import "../style.css";
import "./ExpenseStyles.css";

const AddExpenseForm = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  assets = [],
  selectedAsset = null
}) => {
  const [t] = useTranslation();

  const [formData, setFormData] = useState({
    assetId: selectedAsset?.id || "",
    value: "",
    date: "",
    category: "",
    details: ""
  });

  const [errors, setErrors] = useState({});

  // Update assetId when selectedAsset changes
  useEffect(() => {
    if (selectedAsset) {
      setFormData(prev => ({ ...prev, assetId: selectedAsset.id }));
    }
  }, [selectedAsset]);

  useEffect(() => {
    if (isOpen) {
      // Reset form when opening
      setFormData({
        assetId: selectedAsset?.id || "",
        value: "",
        date: "",
        category: "",
        details: ""
      });
      setErrors({});
    }
  }, [isOpen, selectedAsset]);

  const validate = () => {
    const newErrors = {};

    if (!formData.assetId) {
      newErrors.assetId = t("Please select an asset");
    }

    if (!formData.value || parseFloat(formData.value) <= 0) {
      newErrors.value = t("Amount must be greater than zero");
    }

    if (!formData.date) {
      newErrors.date = t("Date is required");
    }

    if (!formData.category) {
      newErrors.category = t("Please select a category");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Convert data for submission - Must match Android PaymentTransaction format
    const submissionData = {
      assetId: parseInt(formData.assetId, 10),
      amountPaid: parseFloat(formData.value),  // Backend expects "amountPaid"
      paymentDate: new Date(formData.date).getTime(),  // Backend expects "paymentDate"
      category: formData.category,
      description: formData.details  // Backend expects "description"
    };

    onSubmit(submissionData);
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains("modal-overlay")) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content expense-form-modal">
        {/* Modal Header */}
        <div className="modal-header">
          <h2>{t("Add Asset Expense")}</h2>
          <button
            className="modal-close-btn"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <FaTimes />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="asset-form">
          {/* Asset Selection */}
          <div className="form-group">
            <label className="form-label">
              <FaTools /> {t("Asset")} <span className="required">*</span>
            </label>
            <select
              name="assetId"
              value={formData.assetId}
              onChange={handleChange}
              className={`form-input ${errors.assetId ? "error" : ""}`}
              disabled={isSubmitting || !!selectedAsset}
            >
              <option value="">{t("Select an asset")}</option>
              {assets.map(asset => (
                <option key={asset.id} value={asset.id}>
                  {asset.name}
                </option>
              ))}
            </select>
            {errors.assetId && (
              <span className="error-message">{errors.assetId}</span>
            )}
          </div>

          {/* Amount and Date Row */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <FaDollarSign /> {t("Amount")} <span className="required">*</span>
              </label>
              <input
                type="number"
                name="value"
                value={formData.value}
                onChange={handleChange}
                className={`form-input ${errors.value ? "error" : ""}`}
                placeholder="0.00"
                step="0.01"
                min="0"
                disabled={isSubmitting}
              />
              {errors.value && (
                <span className="error-message">{errors.value}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <FaCalendar /> {t("Date")} <span className="required">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className={`form-input ${errors.date ? "error" : ""}`}
                disabled={isSubmitting}
              />
              {errors.date && (
                <span className="error-message">{errors.date}</span>
              )}
            </div>
          </div>

          {/* Category */}
          <div className="form-group">
            <label className="form-label">
              <FaTag /> {t("Category")} <span className="required">*</span>
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className={`form-input ${errors.category ? "error" : ""}`}
              disabled={isSubmitting}
            >
              <option value="">{t("Select a category")}</option>
              {ExpenseCategories.map(category => (
                <option key={category.value} value={category.value}>
                  {t(category.labelEn)}
                </option>
              ))}
            </select>
            {errors.category && (
              <span className="error-message">{errors.category}</span>
            )}
          </div>

          {/* Details */}
          <div className="form-group">
            <label className="form-label">
              <FaStickyNote /> {t("Details")}
            </label>
            <textarea
              name="details"
              value={formData.details}
              onChange={handleChange}
              className="form-input"
              placeholder={t("Add any additional information")}
              rows="4"
              disabled={isSubmitting}
            />
          </div>

          {/* Submit Button */}
          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("Submitting...") : t("Add Expense")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// PropTypes validation
AddExpenseForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  assets: PropTypes.array,
  selectedAsset: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string
  })
};

// Default props
AddExpenseForm.defaultProps = {
  isSubmitting: false,
  assets: [],
  selectedAsset: null
};

export default AddExpenseForm;
